import { useEffect, useState } from 'react';
import { Drawer, Table, Button, Space, Tag, message, Dropdown, Modal, Switch, Typography } from 'antd';
import { DeleteOutlined, InboxOutlined, ReloadOutlined, MoreOutlined, PrinterOutlined, FileTextOutlined, TagOutlined, CheckCircleOutlined, WarningOutlined, SettingOutlined, SyncOutlined, ShoppingCartOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import useScanBufferStore from '../../store/scanBufferStore';
import useBarcodeScanStore from '../../store/barcodeScanStore';
import useScanTargetStore from '../../store/scanTargetStore';
import { barcodeScanApi, type BarcodeScan } from '../../api/barcodeScans';
import { processBarcodeScan } from '../../services/barcodeScanProcessor';
import useIsMobile from '../../hooks/useIsMobile';
import DataMatrixImage from './DataMatrixImage';
import { rawToGs1, hasCryptoTail } from '../../utils/dataMatrix';
import { sellabilityError } from './sellable';
import BrandLogo from '../BrandLogo';
import BarcodeKnowledgeCard from '../barcode/BarcodeKnowledgeCard';
import MarkingPrintPreview from './MarkingPrintPreview';
import MarkingThermalPreview from './MarkingThermalPreview';
import { markingApi } from '../../api/marking';
import type { BufferCode } from '../../api/marking';


import { useNavigate } from 'react-router-dom';

const ScanBufferDrawer: React.FC = () => {
  const navigate = useNavigate();
  const {
    items, loading, selectedCodes, drawerOpen,
    closeDrawer, refreshItems, setSelected, selectAll,
    resolveSelected, deleteSelected,
  } = useScanBufferStore();
  const targetLabel = useScanTargetStore(s => s.label);
  const targetHandler = useScanTargetStore(s => s.handler);
  const targetOrderId = useScanTargetStore(s => s.orderId);
  const targetOrderNumber = useScanTargetStore(s => s.orderNumber);
  const targetMarkingCodes = useScanTargetStore(s => s.markingCodes);
  const targetBarcodeHandler = useScanTargetStore(s => s.barcodeHandler);
  const barcodeItems = useBarcodeScanStore(s => s.items);
  const refreshBarcodeItems = useBarcodeScanStore(s => s.refreshItems);
  const openBarcodeBinding = useBarcodeScanStore(s => s.openBinding);
  const [sendingBarcode, setSendingBarcode] = useState<number | null>(null);

  const handleBarcode = async (scan: BarcodeScan) => {
    setSendingBarcode(scan.id);
    try {
      await processBarcodeScan(scan, true);
    } finally {
      setSendingBarcode(null);
    }
  };

  const dismissBarcode = async (scan: BarcodeScan) => {
    await barcodeScanApi.ack(scan.id, 'dismissed');
    useBarcodeScanStore.getState().removeItem(scan.id);
  };

  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [sendingCode, setSendingCode] = useState<string | null>(null);
  const [retryingGtin, setRetryingGtin] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [thermalPreviewOpen, setThermalPreviewOpen] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [autoAddEnabled, setAutoAddEnabled] = useState(() => {
    try {
      const saved = localStorage.getItem('scanBufferAutoAdd');
      return saved === null ? true : saved === 'true';
    } catch {
      return true;
    }
  });
  const isMobile = useIsMobile();

  const handleToggleAutoAdd = (checked: boolean) => {
    setAutoAddEnabled(checked);
    try {
      localStorage.setItem('scanBufferAutoAdd', String(checked));
    } catch {}
    message.success(checked ? 'Автодобавление включено' : 'Автодобавление отключено');
  };

  // «Повторить НК» — force-запрос в Национальный каталог в обход кэша (rev. 2026-10-16).
  // При успехе backend сам привязывает марки GTIN к карточке каталога.
  const handleRetryNk = async (gtin: string) => {
    setRetryingGtin(gtin);
    try {
      const { data } = await markingApi.suggestGtin(gtin, true);
      if (data.status === 'auto_bound') {
        const name = [data.suggested_brand, data.suggested_model_name, data.suggested_container_size].filter(Boolean).join(' ');
        message.success(`НК нашёл товар: ${name || gtin} — марки привязаны`);
      } else if (data.status === 'needs_review') {
        message.info('НК нашёл товар, но нужна ручная привязка (откройте скан на странице «Сканирование»)');
      } else {
        message.warning(
          data.failure_kind === 'deterministic'
            ? 'НК снова отказал — привяжите GTIN вручную'
            : 'НК не ответил — повторим автоматически в фоне',
        );
      }
      refreshItems();
    } catch {
      message.error('Не удалось повторить запрос в НК');
    } finally {
      setRetryingGtin(null);
    }
  };

  useEffect(() => {
    if (drawerOpen) {
      refreshItems();
      refreshBarcodeItems().catch(() => undefined);
    }
  }, [drawerOpen, refreshBarcodeItems]);

  const handleDeleteSelected = async () => {
    const { deleted, errors } = await deleteSelected();
    if (deleted > 0) message.success(`Убрано из буфера: ${deleted}`);
    errors.forEach((e) => message.error(`${e.marking_code}: ${e.detail}`));
  };

  const showDeleteConfirm = () => {
    Modal.confirm({
      title: 'Убрать из буфера?',
      content: `Будет помечено ${selectedCodes.length} скан(ов)`,
      onOk: handleDeleteSelected,
    });
  };

  const handleAddToBulk = async () => {
    if (selectedCodes.length === 0) {
      message.info('Выберите сканы');
      return;
    }
    const { resolved, already, errors } = await resolveSelected('bulk');
    if (resolved > already.length) message.success(`Добавлено в разливной товар: ${resolved - already.length}`);
    if (already.length > 0) {
      message.info(already.length === 1
        ? 'Марка уже в разливном товаре — время сканирования обновлено, убрана из буфера'
        : `Марки уже в разливном товаре (${already.length}) — убраны из буфера`);
    }
    errors.forEach((e) => message.warning(e.detail || `Не удалось добавить ${e.marking_code}`));
  };

  const handleThermalPrint = async () => {
    if (!guardPrintable()) return;
    setPrinting(true);
    try {
      await markingApi.printThermal(selectedCodes);
      message.success('Печать запущена');
      setThermalPreviewOpen(false);
    } catch (e) {
      message.error('Ошибка запуска печати');
    } finally {
      setPrinting(false);
    }
  };

  const selectedScans = items.filter((s) => selectedCodes.includes(s.marking_code));
  const selectedNoCrypto = selectedScans.filter((s) => !hasCryptoTail(s.raw_code));

  const guardPrintable = (): boolean => {
    if (selectedNoCrypto.length > 0) {
      message.error(
        `Марки без криптохвоста напечатать нельзя (${selectedNoCrypto.length} шт.) — снимите с них выбор`
      );
      return false;
    }
    return true;
  };

  const handleSendToTarget = async (scan: BufferCode) => {
    // rev. 2026-09-18: orderId может быть null (новый несохранённый заказ) —
    // backend-проверки тогда пропускаем, resolveCode выполнится после сохранения
    if (!targetHandler) return;
    setSendingCode(scan.marking_code);
    try {
      if (targetOrderId != null) {
        const check = await markingApi.checkCanAdd(scan.marking_code, targetOrderId);
        if (!check.data.can_add) {
          message.warning(check.data.reason || 'Марку нельзя добавить в заказ');
          setSendingCode(null);
          return;
        }
      }
      const res = await targetHandler(scan);
      if (res.ok) {
        if (targetOrderId != null) {
          try {
            await markingApi.resolveCode(scan.marking_code, {
              action: 'sale',
              order_id: targetOrderId,
              order_number: targetOrderNumber,
            });
          } catch (e: any) {
            if (e?.response?.status === 409) {
              message.warning('Марка уже не в буфере — возможно, она уже добавлена');
            } else {
              throw e;
            }
          }
        }
        if (res.duplicate) {
          message.warning(res.message || `Товар уже в наряде — добавлен дубль`);
        } else {
          message.success(res.message || `Добавлено${targetLabel ? ` → ${targetLabel}` : ''}`);
        }
      } else if (res.duplicate) {
        message.warning(res.message || 'Эта бутылка уже добавлена в заказ-наряд');
      } else {
        message.error(res.message || 'Не удалось добавить');
      }
    } catch (e: any) {
      if (e?.response?.status === 409) {
        message.warning('Марка уже не в буфере — возможно, она уже добавлена');
      } else {
        message.error(e?.response?.data?.detail || 'Ошибка при добавлении');
      }
    } finally {
      setSendingCode(null);
      await refreshItems();
    }
  };

  const handleSendSelectedToTarget = async () => {
    if (!targetHandler) return;
    const scans = items.filter((s) => selectedCodes.includes(s.marking_code));
    let success = 0;
    let failed = 0;
    let duplicates = 0;
    for (const scan of scans) {
      try {
        if (targetOrderId != null) {
          const check = await markingApi.checkCanAdd(scan.marking_code, targetOrderId);
          if (!check.data.can_add) {
            duplicates++;
            continue;
          }
        }
        const res = await targetHandler(scan);
        if (res.ok) {
          if (targetOrderId != null) {
            try {
              await markingApi.resolveCode(scan.marking_code, {
                action: 'sale',
                order_id: targetOrderId,
                order_number: targetOrderNumber,
              });
            } catch (e: any) {
              if (e?.response?.status === 409) {
                // already resolved — count as success
              } else {
                throw e;
              }
            }
          }
          success++;
          if (res.duplicate) {
            duplicates++;
          }
        } else if (res.duplicate) {
          duplicates++;
        } else {
          failed++;
        }
      } catch {
        failed++;
      }
    }
    await refreshItems();
    if (success > 0) {
      if (duplicates > 0) {
        message.warning(`${success} добавлено${targetLabel ? ` → ${targetLabel}` : ''} (${duplicates} дубль)`);
      } else {
        message.success(`${success} добавлено${targetLabel ? ` → ${targetLabel}` : ''}`);
      }
    }
    if (failed > 0) {
      message.error(`${failed} не удалось добавить`);
    }
    if (duplicates > 0 && success === 0) {
      message.warning(`${duplicates} бутылок нельзя добавить — уже в заказе или не найдены в 1С`);
    }
  };

  // Быстрая оплата из меню строки (rev. 2.4 QuickSalePage.md): нельзя — серый
  // пункт с причиной. Правило — единое (sellabilityError, marking_buffer §3)
  const quickSaleDisabledReason = (r: BufferCode): string | null => {
    const err = sellabilityError(r);
    if (!err) return null;
    // Короткие формы причин для пункта меню
    if (r.product_type === 'bulk') return 'разлив — только выбором товара и объёма';
    if (r.order_id != null) return `уже в заказе${r.order_number ? ` № ${r.order_number}` : ''}`;
    if (r.status === 'sold') return 'уже продана';
    if (r.status === 'written_off') return 'списана';
    if (r.status === 'reserved') return 'зарезервирована';
    return 'вне баланса 1С';
  };

  const columns: ColumnsType<BufferCode> = [
    {
      title: 'Товар',
      key: 'product',
      render: (_, r) => {
        const isDuplicate = r.marking_code && targetMarkingCodes.has(r.marking_code);
        const inThisOrder = r.order_id != null && r.order_id === targetOrderId;
        const inOtherOrder = r.order_id != null && r.order_id !== targetOrderId;
        // Единое правило продажи (sellabilityError, marking_buffer §3 rev. 2026-10-23):
        // разлив из буфера не добавляется, непродаваемые статусы (sold и др.) — тоже
        const canAdd = targetHandler && !inThisOrder && !inOtherOrder && !isDuplicate && !sellabilityError(r);
        return (
          <Space size={4} style={{ width: '100%', justifyContent: 'space-between' }}>
            <Space size={4}>
              {r.product_name ? (
                <>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <div className="scan-buffer-product-hover">
                      {r.brand && <BrandLogo brand={r.brand} />}
                      <span>{r.product_name}</span>
                      {r.container_size && <Tag>{r.container_size}</Tag>}
                      {canAdd && (
                        <>
                          <div className="scan-buffer-add-overlay" />
                          <div className="scan-buffer-add-btn">
                            <Button
                              type="primary"
                              size="small"
                              loading={sendingCode === r.marking_code}
                              onClick={(e) => { e.stopPropagation(); handleSendToTarget(r); }}
                              style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                            >
                              Добавить в документ
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                    <div>
                      {r.order_id != null && r.order_number && r.order_id !== targetOrderId && (
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            closeDrawer();
                            navigate(`/service-orders/${r.order_id}`);
                          }}
                          style={{ cursor: 'pointer', display: 'inline-block' }}
                          title="Открыть заказ-наряд"
                        >
                          <Tag color="orange" style={{ cursor: 'pointer' }}>В заказе № {r.order_number}</Tag>
                        </div>
                      )}
                      {((r.order_id != null && r.order_id === targetOrderId) || (r.order_id == null && isDuplicate)) && (
                        <Tag color="red">Уже в этом заказе</Tag>
                      )}
                      {r.status === 'written_off' && (
                        <Tag color="red">Списан</Tag>
                      )}
                      {r.status === 'sold' && (
                        <Tag>Продана</Tag>
                      )}
                      {r.status === 'off_balance' && (
                        <Tag color="orange">Вне баланса 1С</Tag>
                      )}
                      {r.product_type === 'unknown' && (
                        <Tag color="orange">Требуется привязка товара/тары</Tag>
                      )}
                      {r.product_type === 'bulk' && (
                        <Tag color="blue" title="Разливной товар добавляется в заказ-наряд по номенклатуре (товар + объём), не из буфера">
                          Разливной — по номенклатуре
                        </Tag>
                      )}
                      {r.product_type === 'bulk' && r.bulk_remaining_volume != null && (
                        <Tag color="geekblue">ост {Number(r.bulk_remaining_volume)} л</Tag>
                      )}
                      {!hasCryptoTail(r.raw_code) && (
                        <Tag color="orange" title="Марка из ШУТ 1С, без криптохвоста — печать запрещена">
                          без криптохвоста
                        </Tag>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <Space size={4} wrap>
                  <Tag color="orange">GTIN: {r.gtin || '—'}</Tag>
                  {r.gtin_failure_kind === 'deterministic' && (
                    <Tag color="red">НК не нашёл — привяжите вручную</Tag>
                  )}
                  {r.gtin_failure_kind === 'transient' && (
                    <Tag color="orange">НК не ответил — повторим автоматически</Tag>
                  )}
                  {r.gtin && (
                    <Button
                      type="link"
                      size="small"
                      icon={<SyncOutlined />}
                      loading={retryingGtin === r.gtin}
                      onClick={(e) => { e.stopPropagation(); handleRetryNk(r.gtin!); }}
                      title="Повторить запрос в Национальный каталог в обход кэша"
                    >
                      Повторить НК
                    </Button>
                  )}
                </Space>
              )}
              {/* Статус марки */}
              {r.status === 'ready' ? (
                <CheckCircleOutlined style={{ color: '#52c41a' }} title="Готова к продаже" />
              ) : r.order_id != null || r.status === 'reserved' ? (
                <WarningOutlined style={{ color: '#faad14' }} title="В заказе" />
              ) : (
                <WarningOutlined style={{ color: '#bfbfbf' }} title={r.status} />
              )}
            </Space>
          </Space>
        );
      },
    },
    {
      title: 'Время',
      dataIndex: 'scanned_at',
      width: 120,
      render: (v: string) => new Date(v).toLocaleString('ru-RU', { timeStyle: 'short', dateStyle: 'short' }),
    },
    {
      title: '',
      key: 'actions',
      width: 40,
      render: (_, r) => (
        <Dropdown
          menu={{
            items: [
              (() => {
                const reason = quickSaleDisabledReason(r);
                return {
                  key: 'quick-sale',
                  label: reason ? `Быстрая оплата (${reason})` : 'Быстрая оплата',
                  icon: <ShoppingCartOutlined />,
                  disabled: !!reason,
                };
              })(),
              { key: 'bulk', label: 'В разливной товар', icon: <InboxOutlined /> },
              { key: 'delete', label: 'Скрыть из буфера', icon: <DeleteOutlined /> },
            ],
            onClick: async ({ key }) => {
              if (key === 'quick-sale') {
                // Без выделения — в продажу только эта марка; с выделением —
                // обычная логика по выбранным (rev. 2.4). closeDrawer сбрасывает
                // selectedCodes — восстанавливаем (rev. 2.3)
                const sel = selectedCodes;
                closeDrawer();
                setSelected(sel.length > 0 ? sel : [r.marking_code]);
                navigate('/quick-sale');
                return;
              }
              setSelected([r.marking_code]);
              if (key === 'bulk') {
                const { resolved, already, errors } = await resolveSelected('bulk');
                if (resolved > already.length) message.success('Добавлено в разливной товар');
                if (already.length > 0) message.info('Марка уже в разливном товаре — время сканирования обновлено, убрана из буфера');
                errors.forEach((e) => message.warning(e.detail || 'Не удалось добавить'));
              } else if (key === 'delete') {
                try {
                  await markingApi.removeFromBuffer(r.marking_code);
                  message.success('Скан убран из буфера');
                  refreshItems();
                } catch (e: any) {
                  message.error(e?.response?.data?.detail || 'Не удалось убрать скан из буфера');
                }
              }
            },
          }}
        >
          <Button type="text" icon={<MoreOutlined />} size="small" />
        </Dropdown>
      ),
    },
  ];

  const selectedHasNonBuffer = selectedCodes.some(code => {    const scan = items.find(s => s.marking_code === code);
    return scan?.order_id != null && scan?.order_id !== targetOrderId;
  });

  const selectedHasDuplicate = selectedCodes.some(code => {
    const scan = items.find(s => s.marking_code === code);
    return (scan?.marking_code && targetMarkingCodes.has(scan.marking_code)) ||
           (scan?.order_id != null && scan?.order_id === targetOrderId);
  });

  return (
    <>
      <style>{`
        @keyframes glow-pulse-orange {
          0% { box-shadow: 0 0 0 0 rgba(250, 173, 20, 0.7); }
          70% { box-shadow: 0 0 0 10px rgba(250, 173, 20, 0); }
          100% { box-shadow: 0 0 0 0 rgba(250, 173, 20, 0); }
        }
      `}</style>
      <Drawer
        title={`Буфер сканирования · ${items.length + barcodeItems.length}`}
        placement="right"
        width={isMobile ? '100vw' : 480}
        open={drawerOpen}
        onClose={closeDrawer}
        mask={isMobile}
        extra={
        <Space>
          <Dropdown
            menu={{
              items: [
                // Быстрая оплата — розничная продажа товаров из буфера/каталога
                // без шапки заказа (QuickSalePage.md): order_type='quick_sale'
                { key: 'quick-sale', label: 'Быстрая оплата', icon: <ShoppingCartOutlined /> },
                { type: 'divider' },
                { key: 'revalidate', label: 'Перепроверить марки', icon: <SyncOutlined /> },
                { type: 'divider' },
                { key: 'settings', label: 'Настройки...', icon: <SettingOutlined /> },
              ],
              onClick: async ({ key }) => {
                if (key === 'quick-sale') {
                  // closeDrawer() сбрасывает selectedCodes — сохраняем выделение,
                  // QuickSalePage префиллится по нему (rev. 2.3)
                  const sel = selectedCodes;
                  closeDrawer();
                  if (sel.length > 0) setSelected(sel);
                  navigate('/quick-sale');
                  return;
                }
                if (key === 'settings') setSettingsOpen(true);
                if (key === 'revalidate') {
                  try {
                    const res = await markingApi.revalidateBuffer();
                    message.success(`Проверено ${res.data.checked} марок, сброшено в буфер: ${res.data.reset}`);
                    await refreshItems();
                  } catch (e: any) {
                    message.error(e?.response?.data?.detail || 'Ошибка при перепроверке');
                  }
                }
              },
            }}
            trigger={['click']}
          >
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
          <Button
            type="text"
            icon={<ReloadOutlined />}
            onClick={() => { refreshItems(); refreshBarcodeItems().catch(() => undefined); }}
          />
        </Space>
      }
    >
      <Space style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }}>
        <Space size={4}>
          <Button size="small" onClick={selectAll} disabled={items.length === 0}>Выбрать все</Button>
          <Button
            size="small"
            icon={<DeleteOutlined />}
            danger
            disabled={selectedCodes.length === 0}
            onClick={showDeleteConfirm}
          >
            Удалить
          </Button>
          {targetHandler && selectedCodes.length > 0 && (
            <Button
              type="primary"
              size="small"
              onClick={handleSendSelectedToTarget}
              disabled={selectedHasNonBuffer || selectedHasDuplicate}
              title={selectedHasNonBuffer ? 'Некоторые марки не в буфере (уже в заказе)' : selectedHasDuplicate ? 'Некоторые марки уже в этом заказе' : undefined}
              style={selectedHasNonBuffer || selectedHasDuplicate
                ? { backgroundColor: '#bfbfbf', borderColor: '#bfbfbf' }
                : { backgroundColor: '#52c41a', borderColor: '#52c41a' }
              }
            >
              Добавить в документ ({selectedCodes.length})
            </Button>
          )}
        </Space>
        <Dropdown
          disabled={selectedCodes.length === 0}
          menu={{
            items: [
              // Быстрая оплата выбранных сканов (QuickSalePage.md §2):
              // продажа префиллится только выбранными галочками марками
              { key: 'quick-sale', label: 'Быстрая оплата', icon: <ShoppingCartOutlined /> },
              { type: 'divider' },
              { key: 'sheet', label: 'Лист (А4)', icon: <FileTextOutlined /> },
              { key: 'thermal', label: 'Марки (20×20 мм)', icon: <TagOutlined /> },
              { type: 'divider' },
              { key: 'bulk', label: 'В разливной товар', icon: <InboxOutlined /> },
              { type: 'divider' },
              { key: 'delete', label: 'Удалить', icon: <DeleteOutlined />, danger: true },
            ],
            onClick: ({ key }) => {
              if (key === 'quick-sale') {
                // closeDrawer() сбрасывает selectedCodes — сохраняем выделение (rev. 2.3)
                const sel = selectedCodes;
                closeDrawer();
                if (sel.length > 0) setSelected(sel);
                navigate('/quick-sale');
                return;
              }
              if (key === 'sheet' && guardPrintable()) setPreviewOpen(true);
              if (key === 'thermal' && guardPrintable()) setThermalPreviewOpen(true);
              if (key === 'bulk') handleAddToBulk();
              if (key === 'delete') showDeleteConfirm();
            },
          }}
          trigger={['click']}
        >
          <Button
            icon={<MoreOutlined />}
            size="small"
            disabled={selectedCodes.length === 0}
          />
        </Dropdown>
      </Space>

      {barcodeItems.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <Typography.Text strong>Обычные штрихкоды</Typography.Text>
          {barcodeItems.map(scan => (
            <div
              key={scan.id}
              style={{ padding: '10px 0', borderBottom: '1px solid #f0f0f0', display: 'flex', flexDirection: 'column', gap: 6 }}
            >
              <Space size={6} wrap>
                <Tag color="blue">Штрихкод</Tag>
                <Typography.Text code>{scan.raw_code}</Typography.Text>
              </Space>
              {scan.product && <Typography.Text>{scan.product.name}</Typography.Text>}
              {(scan.knowledge || scan.product?.knowledge) && (
                <BarcodeKnowledgeCard knowledge={(scan.knowledge || scan.product?.knowledge)!} compact />
              )}
              {scan.outcome === 'awaiting_binding' && <Tag color="orange">Требуется привязка</Tag>}
              {scan.outcome === 'rejected' && <Tag color="red">Не добавлен — проверьте причину</Tag>}
              <Space size={4} wrap>
                {scan.outcome === 'awaiting_binding' && (
                  <Button size="small" onClick={() => openBarcodeBinding(scan)}>
                    Привязать к товару
                  </Button>
                )}
                {targetBarcodeHandler && (
                  <Button
                    type="primary"
                    size="small"
                    loading={sendingBarcode === scan.id}
                    onClick={() => handleBarcode(scan)}
                    style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                  >
                    Добавить в документ
                  </Button>
                )}
                <Button type="text" size="small" onClick={() => dismissBarcode(scan)}>
                  Скрыть
                </Button>
              </Space>
            </div>
          ))}
        </div>
      )}

      <Table
        rowKey="marking_code"
        size="small"
        columns={columns}
        dataSource={items}
        loading={loading}
        pagination={false}
        rowSelection={{
          selectedRowKeys: selectedCodes,
          onChange: (keys) => setSelected(keys as string[]),
        }}
        onRow={(record) => ({
          onClick: (e) => {
            if ((e.target as HTMLElement).closest('.ant-table-selection-column, button, .ant-dropdown')) return;
            setExpandedKeys((prev) => prev.includes(record.marking_code) ? [] : [record.marking_code]);
          },
          style: {
            cursor: 'pointer',
            ...((record.marking_code && targetMarkingCodes.has(record.marking_code)) ||
               (record.order_id != null && record.order_id === targetOrderId)
              ? { animation: 'glow-pulse-orange 1.5s ease-in-out infinite', outline: '2px solid #faad14', outlineOffset: '-2px', borderRadius: 4 }
              : {}),
          },
        })}
        expandable={{
          expandedRowKeys: expandedKeys,
          showExpandColumn: false,
          expandedRowRender: (record) => {
            const displayCode = rawToGs1(record.raw_code) ?? record.raw_code;
            // Марка без криптохвоста — текст красным (rev. 2026-09-17)
            const noCrypto = !hasCryptoTail(record.raw_code);
            return (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 8, gap: 12 }}>
                <DataMatrixImage code={record.raw_code} scale={2} />
                <div style={{ maxWidth: 360, width: '100%', textAlign: 'center' }}>
                  <Typography.Text
                    copyable={{ text: displayCode, tooltips: ['Копировать для ЧЗ/CSV', 'Скопировано'] }}
                    style={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all', ...(noCrypto ? { color: '#ff4d4f' } : {}) }}
                  >
                    {displayCode}
                  </Typography.Text>
                </div>
              </div>
            );
          },
        }}
        locale={{ emptyText: 'Буфер пуст — отсканируйте коды с телефона' }}
      />
      <MarkingPrintPreview
        open={previewOpen}
        scans={selectedScans}
        onClose={() => setPreviewOpen(false)}
      />
      <MarkingThermalPreview
        open={thermalPreviewOpen}
        scans={selectedScans}
        onClose={() => setThermalPreviewOpen(false)}
        onPrint={handleThermalPrint}
        printing={printing}
      />
      <Modal
        title="Настройки буфера"
        open={settingsOpen}
        onCancel={() => setSettingsOpen(false)}
        footer={null}
        width={360}
      >
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontWeight: 500 }}>Сразу добавлять</span>
            <Switch checked={autoAddEnabled} onChange={handleToggleAutoAdd} />
          </div>
          <Typography.Text type="secondary" style={{ fontSize: 13 }}>
            При сканировании товара сразу добавлять в документ, иначе оставлять в буфере
          </Typography.Text>
        </div>
      </Modal>
    </Drawer>
    </>
  );
};

export default ScanBufferDrawer;
